/**
 * Reusable touch swipe gesture handlers
 * Extracted from settings-modal.js for reuse across modals and components
 */

/**
 * Add horizontal swipe support to an element
 * @param {HTMLElement} element - The element to attach swipe handlers to
 * @param {function('left'|'right'): void} onSwipe - Callback when swipe is detected
 * @returns {function(): void} Cleanup function to remove event listeners
 */
export function addSwipeSupport(element, onSwipe) {
    if (!element || typeof onSwipe !== 'function') return () => {};

    // Help browsers that support it (mostly Pointer Events). Harmless elsewhere.
    try {
        if (!element.style.touchAction) element.style.touchAction = 'pan-y';
    } catch (_) {}

    const config = {
        minDistanceX: 40,      // pixels
        minDistanceRatio: 1.15, // abs(dx) must be >= abs(dy) * ratio
        maxDurationMs: 900     // ms; 300ms was too strict for real swipes in a modal
    };

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    let isTracking = false;
    let didLockHorizontal = false;

    const reset = () => {
        isTracking = false;
        didLockHorizontal = false;
        touchStartX = 0;
        touchStartY = 0;
        touchStartTime = 0;
    };

    const handleTouchStart = (e) => {
        if (!e.touches || e.touches.length !== 1) return;
        const t = e.touches[0];
        touchStartX = t.clientX;
        touchStartY = t.clientY;
        touchStartTime = Date.now();
        isTracking = true;
        didLockHorizontal = false;
    };

    // Lock horizontal swipes so the modal doesn't steal the gesture as scroll.
    const handleTouchMove = (e) => {
        if (!isTracking || !e.touches || e.touches.length !== 1) return;
        const t = e.touches[0];
        const dx = t.clientX - touchStartX;
        const dy = t.clientY - touchStartY;

        if (didLockHorizontal) {
            e.preventDefault();
            return;
        }

        // Only lock once the user is clearly swiping horizontally.
        if (Math.abs(dx) >= 12 && Math.abs(dx) >= Math.abs(dy) * config.minDistanceRatio) {
            didLockHorizontal = true;
            e.preventDefault();
        }
    };

    const handleTouchEnd = (e) => {
        if (!isTracking || !e.changedTouches || e.changedTouches.length !== 1) {
            reset();
            return;
        }

        const t = e.changedTouches[0];
        const touchEndTime = Date.now();
        const dx = t.clientX - touchStartX;
        const dy = t.clientY - touchStartY;
        const dt = touchEndTime - touchStartTime;

        reset();

        if (dt > config.maxDurationMs) return;
        if (Math.abs(dx) < config.minDistanceX) return;
        if (Math.abs(dx) < Math.abs(dy) * config.minDistanceRatio) return;

        onSwipe(dx > 0 ? 'right' : 'left');
    };

    const handleTouchCancel = () => {
        reset();
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    element.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    // Return cleanup function
    return () => {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
        element.removeEventListener('touchend', handleTouchEnd);
        element.removeEventListener('touchcancel', handleTouchCancel);
    };
}

/**
 * Add vertical swipe support to an element
 * @param {HTMLElement} element - The element to attach swipe handlers to
 * @param {function('up'|'down'): void} onSwipe - Callback when swipe is detected
 * @returns {function(): void} Cleanup function to remove event listeners
 */
export function addVerticalSwipeSupport(element, onSwipe) {
    if (!element || typeof onSwipe !== 'function') return () => {};

    const config = {
        minDistanceY: 40,
        minDistanceRatio: 1.15,
        maxDurationMs: 900
    };

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    let isTracking = false;
    let didLockVertical = false;

    const reset = () => {
        isTracking = false;
        didLockVertical = false;
        touchStartX = 0;
        touchStartY = 0;
        touchStartTime = 0;
    };

    const handleTouchStart = (e) => {
        if (!e.touches || e.touches.length !== 1) return;
        const t = e.touches[0];
        touchStartX = t.clientX;
        touchStartY = t.clientY;
        touchStartTime = Date.now();
        isTracking = true;
        didLockVertical = false;
    };

    const handleTouchMove = (e) => {
        if (!isTracking || !e.touches || e.touches.length !== 1) return;
        const t = e.touches[0];
        const dx = t.clientX - touchStartX;
        const dy = t.clientY - touchStartY;

        if (didLockVertical) {
            e.preventDefault();
            return;
        }

        if (Math.abs(dy) >= 12 && Math.abs(dy) >= Math.abs(dx) * config.minDistanceRatio) {
            didLockVertical = true;
            e.preventDefault();
        }
    };

    const handleTouchEnd = (e) => {
        if (!isTracking || !e.changedTouches || e.changedTouches.length !== 1) {
            reset();
            return;
        }

        const t = e.changedTouches[0];
        const touchEndTime = Date.now();
        const dx = t.clientX - touchStartX;
        const dy = t.clientY - touchStartY;
        const dt = touchEndTime - touchStartTime;

        reset();

        if (dt > config.maxDurationMs) return;
        if (Math.abs(dy) < config.minDistanceY) return;
        if (Math.abs(dy) < Math.abs(dx) * config.minDistanceRatio) return;

        onSwipe(dy > 0 ? 'down' : 'up');
    };

    const handleTouchCancel = () => {
        reset();
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    element.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    // Return cleanup function
    return () => {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
        element.removeEventListener('touchend', handleTouchEnd);
        element.removeEventListener('touchcancel', handleTouchCancel);
    };
}
